import moment from 'moment';
import Inventory from '../patient-summary/inventory';
import app from '../../app';
import {ucfirst} from "../../../plugins/uniweb";

export const prescriptionRules = (state) => state.prescriptionRules;

export const columns = () => ([
  {
    name: 'intensity',
    icon: 'mdi-emoticon-dead-outline'
  },
  {
    name: 'frequency',
    icon: 'mdi-chart-line',
  },
  {
    name: 'sessionDuration',
    icon: 'mdi-timer-outline',
  },
  {
    name: 'programDuration',
    icon: 'mdi-calendar-month-outline',
  },
  {
    name: 'strengthTraining',
    icon: 'mdi-dumbbell',
  },
]);

export const selectedRules = (state) => {
  const rules = state.selectedRules || [];
  const defaultRules = state.defaultRules || [];

  if (rules.includes("MEDICATION__STATIN") && !rules.includes("RISK_FACTOR__DISLIPIDEMIA")){
    rules.push("RISK_FACTOR__DISLIPIDEMIA")
  }
  if (rules.includes("MEDICATION__BETA_BLOCKER") && !rules.includes("RISK_FACTOR__HYPERTENSION")){
    rules.push("RISK_FACTOR__HYPERTENSION")
  }
  if (rules.includes("MEDICATION__SULFONYLUREA") && !rules.includes("RISK_FACTOR__TYPE2_DIABETES")){
    rules.push("RISK_FACTOR__TYPE2_DIABETES")
  }

  // all default rules should always be selected
  defaultRules.forEach(defRule => {
    if (!rules.includes(defRule)) {
      rules.push(defRule)
    }
  })

  return rules;
}

export const selectedRulesDetails = (state, getters) => getters
  .selectedRules.reduce((accumulator, currentKey) => {
    const rule = getters.prescriptionRules
      .find((item) => item.key === currentKey);

    if (rule) {
      return [...accumulator, rule];
    }

    return accumulator;
  }, []);

export const selectedRuleNamesByType = (state, getters) => ({
  'PRIMARY_INDICATION': getters.selectedRulesDetails
  .filter((r) => r.type === 'PRIMARY_INDICATION').map((r) => r.name),
  'RISK_FACTOR': getters.selectedRulesDetails
  .filter((r) => r.type === 'RISK_FACTOR').map((r) => r.name),
  'EXERCISE_MODIFIER': getters.selectedRulesDetails
  .filter((r) => r.type === 'EXERCISE_MODIFIER').map((r) => r.name),
  'ANOMALY': getters.selectedRulesDetails
  .filter((r) => r.type === 'ANOMALY').map((r) => r.name),
  'MEDICATION': getters.selectedRulesDetails
  .filter((r) => r.type === 'MEDICATION').map((r) => r.name),
});

export const hrAtrialFibrilationProvoked = (state) => state.hrAtrialFibrilationProvoked;

export const hrMyocardialIschemicTreshold = (state) => state.hrMyocardialIschemicTreshold;

export const hrVentricularTachycardia = (state) => state.hrVentricularTachycardia;

export const hrICDTreshold = (state) => state.hrICDTreshold;

export const previousRecommendation = (state) => state.previousRecommendation || {};

export const hasPreviousRecommendation = (state, getters) => !!getters.previousRecommendation.id;

export const recommendation = (state) => state.recommendation || {};

export const vanillaRecommendation = (state) => state.vanillaRecommendation ? JSON.parse(state.vanillaRecommendation) : null;

export const hasRecommendation = (state, getters) => {
  const { recommendation } = getters;
  return (recommendation.heartRate || recommendation.intensityLabel || (recommendation.intensities && recommendation.intensities.length))
    && (recommendation.minFrequency || recommendation.maxFrequency || (recommendation.frequencyFlags && recommendation.frequencyFlags.length))
    && (recommendation.minSessionDuration || recommendation.maxSessionDuration || (recommendation.sessionDurationFlags && recommendation.sessionDurationFlags.length))
    && (recommendation.minProgramDuration || recommendation.maxProgramDuration || (recommendation.programDurationFlags && recommendation.programDurationFlags.length))
    && (recommendation.strengthTrainingFlags && recommendation.strengthTrainingFlags.length);
}

function markdownToList(md) {
  return md
    .replaceAll('** ', "")
    .replaceAll('* *', "<br>")
    .split('* ')
    .filter((line) => line.replace(/\s/g,'') !== '');
}

export const safetyPrecautions = (getters) => {
  const rules = getters.previousRecommendation.selectedRules || [];
  const allSafetyPrecautions = rules.reduce((items, item) => {
    if (item.safetyPrecautions && item.safetyPrecautions.length) {
      return [...items, {
        name: item.name,
        items: markdownToList(item.safetyPrecautions.reduce((md, item) => md + item.text + ' ', '')),
      }];
    }
    return items;
  }, []);

  // remove duplicates based on name
  const uniqueSafetyPrecautions = allSafetyPrecautions.filter((item, index, self) => {
    return self.findIndex(it => item.name === it.name) === index
  });
  return uniqueSafetyPrecautions;
}

export const isPrescriptionIntensityValid = (state, getters) => {
  const { recommendation } = getters;

  if (!recommendation.intensities || !recommendation.intensities.length) {
    return false;
  }

  const [intensity] = recommendation.intensities;

  return !!intensity.min || !!intensity.max;
}

export const arePrescriptionHeartRatesValid = (state, getters) => {
  const { recommendation } = getters;

  if (recommendation.hasHIT) {
    return !!+recommendation.heartRate && !!+recommendation.heartRateCooldown
  }
  return true
}

export const isPrescriptionFrequencyValid = (state, getters) => {
  const { recommendation } = getters;
  const { minFrequency, maxFrequency } = recommendation;

  return !!minFrequency || !!maxFrequency;
}

export const isPrescriptionSessionDurationValid = (state, getters) => {
  const { recommendation } = getters;
  const { minSessionDuration, maxSessionDuration } = recommendation;

  return !!minSessionDuration || !!maxSessionDuration;
};

export const isPrescriptionProgramDurationValid = (state, getters) => {
  const { recommendation } = getters;
  const { minProgramDuration, maxProgramDuration } = recommendation;

  return !!minProgramDuration || !!maxProgramDuration;
};

export const isPrescriptionStrengthTrainingValid = (state, getters) => {
  const { recommendation } = getters;
  const flags = recommendation.strengthTrainingFlags || [];
  return (flags.includes('YES') || flags.includes('NO'))
}

export const isPrescriptionValid = (state, getters) => {
  return [
    getters.isPrescriptionIntensityValid,
    getters.isPrescriptionFrequencyValid,
    getters.isPrescriptionSessionDurationValid,
    getters.isPrescriptionProgramDurationValid,
    getters.isPrescriptionStrengthTrainingValid,
    getters.arePrescriptionHeartRatesValid
  ].every((item) => item);
};

export const isRecommendationChanged = (state, getters) => getters.recommendation.changed;

export const isRecommendationSaved = (state) => state.isRecommendationSaved;

export const startRecommendationNextWeek = (state) => state.startRecommendationNextWeek;

export const prescriptionHistory = (state) => state.history.prescriptions || [];

export const selectedPrescriptionId = (state) => state.history.selectedPrescriptionId;

export const hasPreviousPrescription = (state, getters) => !!getters.prescriptionHistory.length;

export const previousPrescription = (state, getters) => getters
  .hasPreviousPrescription ? getters.prescriptionHistory[0] : {};

export const selectedPrescription = (state, getters) => {
  const { selectedPrescriptionId } = state.history;

  if (!selectedPrescriptionId) return null;

  const prescription = getters.prescriptionHistory
    .find((item) => item.id === selectedPrescriptionId);

  return prescription || null;
}

export const indicationCategories = (state, getters) => [
  {
    name: 'expertTool.primaryIndication.title',
    message: 'expertTool.primaryIndication.message',
    color: '#693160',
    component: 'prescription-rules-table',
    props: { ruleType: 'PRIMARY_INDICATION' },
  },
  {
    name: 'expertTool.keyRiskFactor.title',
    message: 'expertTool.keyRiskFactor.message',
    color: '#982c5e',
    component: 'prescription-rules-table',
    props: { ruleType: 'RISK_FACTOR' },
  },
  {
    name: 'expertTool.exerciseModifier.title',
    message: 'expertTool.exerciseModifier.message',
    color: '#c41a30',
    component: 'prescription-rules-table',
    props: { ruleType: 'EXERCISE_MODIFIER' },
  },
  {
    name: 'expertTool.anomalies.title',
    message: 'expertTool.anomalies.message',
    color: '#f87335',
    component: 'anomalies',
    props: { ruleType: 'ANOMALY' },
  },
  {
    name: 'questionnaires.INFORMATION_PREFERENCES.topic.MEDICATION',
    catalog: "questionnaires",
    message: 'expertTool.medication.message',
    color: '#fc9d3c',
    component: 'medication',
    props: { ruleType: 'MEDICATION' },
  },
];

export const previousPrescriptionDate = (state, getters) => {
  if (!getters.hasPreviousPrescription) return '';

  const { createdAt } = getters.previousPrescription;

  if (createdAt) {
    return moment(createdAt).format('DD-MM-YYYY');
  }

  return '';
}

export const selectedPrescriptionDate = (state, getters) => {
  if (!getters.selectedPrescriptionId) return '';

  const { createdAt } = getters.selectedPrescription;

  if (createdAt) {
    return moment(createdAt).format('DD-MM-YYYY');
  }

  return '';
}

export const selectedPrescriptionPatientSummary = (state) => state.history
  .selectedPrescriptionPatientSummary || {};


export const selectedPrescriptionPatientBloodPressure = (state, getters) => {
  const { bloodPressure } = getters.selectedPrescriptionPatientSummary;
  if (!bloodPressure || !bloodPressure.component) return '';

  const systolicComponent = bloodPressure.component[0];
  const diastolicComponent = bloodPressure.component[1];

  if (!systolicComponent || !diastolicComponent) return '';

  const systolicValue = systolicComponent.valueQuantity.value;
  const diastolicValue = diastolicComponent.valueQuantity.value;

  return `${systolicValue}/${diastolicValue}`;
};

export const selectedPrescriptionPatientWeight = (state, getters) => {
  const { weight } = getters.selectedPrescriptionPatientSummary;
  if (!weight || !weight.valueQuantity) return '';

  const { value } = weight.valueQuantity;
  return value || '';
};

export const selectedPrescriptionPatientCholesterolLDL = (state, getters) => {
  const { LDL } = getters.selectedPrescriptionPatientSummary;
  if (!LDL || !LDL.valueQuantity) return '';

  const { value } = LDL.valueQuantity;
  return value || '';
};

export const selectedPrescriptionPatientGlucose = (state, getters) => {
  const { HbA1c } = getters.selectedPrescriptionPatientSummary;
  if (!HbA1c || !HbA1c.valueQuantity) return '';

  const { value } = HbA1c.valueQuantity;
  return value || '';
}

export const selectedPrescriptionPatientBMI = (state, getters) => {
  const { BMI } = getters.selectedPrescriptionPatientSummary;
  if (!BMI || !BMI.valueQuantity) return '';

  const { value } = BMI.valueQuantity;
  return value || '';
};

export const selectedPrescriptionPatientBMIDate = (state, getters) => {
  const { BMI } = getters.selectedPrescriptionPatientSummary;
  if (!BMI || !BMI.effectiveDateTime) return '';

  return BMI.effectiveDateTime;
}

export const selectedPrescriptionPatientKnowledgeLevel = (state, getters) => {
  const { user } = getters.selectedPrescriptionPatientSummary;
  if (!user || !user.knowledgeLevel) return {};

  return user.knowledgeLevel;
}

export const selectedPrescriptionPatientKnowledgeLevelName = (state, getters) => {
  return getters.selectedPrescriptionPatientKnowledgeLevel.name || '';
}

export const selectedPrescriptionPatientBehaviourGoals = (state, getters) => {
  const { behaviourGoals } = getters.selectedPrescriptionPatientSummary;
  return new Inventory(behaviourGoals);

};
export const selectedPrescriptionPatientOutcomeGoals = (state, getters) => {
  const { outcomeGoals } = getters.selectedPrescriptionPatientSummary;
  return new Inventory(outcomeGoals);
};

export const selectedPrescriptionPatientHeartRate = (state, getters) => {
  const { heartRate } = getters.selectedPrescriptionPatientSummary;
  if (!heartRate || !heartRate.valueQuantity) return '';

  const { value } = heartRate.valueQuantity;
  return value || '';
}

export const selectedPrescriptionPatientDistanceWalked = (state, getters) => {
  const { lastCompletedSixMinuteWalkingTest } = getters.selectedPrescriptionPatientSummary;
  if (!lastCompletedSixMinuteWalkingTest || !lastCompletedSixMinuteWalkingTest.distanceWalked) {
    return '';
  }

  return lastCompletedSixMinuteWalkingTest.distanceWalked || '';
};

export const getAdditionalTrainingStrategiesPlusDetails = (state, getters) => (givenRecommendation) => {
  const recommendation = givenRecommendation || getters.recommendation;
  let additionalTrainingStrategies = recommendation.algorithmRan ?
      addBulletPoints(recommendation.additionalTrainingStrategies) :
      recommendation.additionalTrainingStrategies;

  if (typeof additionalTrainingStrategies === 'undefined' || additionalTrainingStrategies === null) {
    additionalTrainingStrategies = '';
  }

  const hiit = app.translationManager.t('expertTool.intensity.HIT.short');

  const strengthTrainingDetails = recommendation.strengthTrainingDetails;

  if (typeof strengthTrainingDetails !== 'undefined' && strengthTrainingDetails !== null) {
    additionalTrainingStrategies += '\r\n• '
      + app.translationManager.t('pa.strengthTrainingExercises') + ':\r\n'
      + strengthTrainingDetails;
  }

  const intensities = recommendation.extraInfoIntensities;

  if (typeof intensities !== 'undefined' && intensities !== null) {
    additionalTrainingStrategies += '\r\n• ' + hiit + ' '
      + app.translationManager.t('expertTool.columnNames.intensity') + ':\r\n'
      + intensities;
  }
  const sessions = recommendation.extraInfoSessions;

  if (typeof sessions !== 'undefined' && sessions !== null) {
    additionalTrainingStrategies += '\r\n• ' + hiit + ' '
      + ucfirst(app.translationManager.t('sessions')) + ':\r\n'
      + sessions;
  }

  const frequencies = recommendation.extraInfoFrequencies;

  if (typeof frequencies !== 'undefined' && frequencies !== null) {
    additionalTrainingStrategies += '\r\n• ' + hiit + ' '
      + app.translationManager.t('expertTool.columnNames.frequency', { catalog: 'app_dashboard' }) + ':\r\n'
      + frequencies;
  }

  return additionalTrainingStrategies;
}

const addBulletPoints = (string) => {
  if (!string) {
    return string
  }
  const newString = string.replace(/\n/gm, '\n • ')
  return ' • ' + newString;
}

export const recommendationUpdatedByAlgorithm = (state) => {
  return state.recommendationUpdatedByAlgorithm;
};

export const recommendationReverted = (state) => {
  return state.recommendationReverted;
};